import React from "react";
import { Box, Dialog, Divider, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import LoadingModal from "./loading-modal";
import { v4 as uuidv4 } from "uuid";
import { getRequest } from "@/utils/request";

interface BarcodeModalProps {
  open: boolean;
  onClose: () => void;
  productImage: string;
  lang: string;
  uuid: string;
  orderId: string;
}

const BarcodeModal: React.FC<BarcodeModalProps> = ({
  open,
  onClose,
  productImage,
  lang,
  uuid,
  orderId,
}) => {
  const { i18n } = useLingui();
  const { data, isLoading } = useQuery({
    queryKey: ["barcode", uuid],
    queryFn: () =>
      getRequest({
        path: { name: "voucherBarcode", id: `uuid/${uuid}/order/${orderId}` },
        lang,
        skipCache: true,
      }),
    enabled: open,
  });

  return isLoading ? (
    <LoadingModal handleClose={onClose} />
  ) : (
    <Dialog open={open} onClose={onClose} sx={{ "& .MuiDialog-paper": { borderRadius: "10px" } }}>
      <Box sx={{ pt: 0, px: "25px", pb: "25px", position: "relative" }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          data-testid="close-button"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "inherit",
          }}
        >
          <CloseIcon />
        </IconButton>
        <Image
          src={productImage}
          alt={t(i18n)`Product image`}
          width={620}
          height={414}
          style={{ maxWidth: "100%", height: "auto" }}
        />
        {data?.data.items
          ?.sort((a: { sort_order: string }, b: { sort_order: string }) => {
            const parsedA = parseInt(a.sort_order, 10);
            const parsedB = parseInt(b.sort_order, 10);
            return parsedA - parsedB;
          })
          .map((item: { content: string; content_type: string }, index: number) => (
            <Box key={uuidv4()} sx={{ mb: 2 }}>
              {index > 0 && <Divider sx={{ mb: "20px" }} />}
              {item.content_type === "qrcode" ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: "100%" }}
                >
                  <Image
                    src={`data:image/png;base64,${item.content}`}
                    alt={t(i18n)`Qrcode image`}
                    width={100}
                    height={100}
                    style={{ objectFit: "contain", display: "block", margin: "0 auto" }}
                  />
                </Box>
              ) : item.content_type === "codei25" || item.content_type === "code128" ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: "100%" }}
                >
                  <Image
                    src={`data:image/png;base64,${item.content}`}
                    alt={t(i18n)`Barcode image`}
                    width={250}
                    height={50}
                    style={{
                      display: "block",
                      margin: "0 auto",
                      height: "70px",
                    }}
                  />
                </Box>
              ) : (
                <Typography
                  variant="body1"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                  sx={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                />
              )}
            </Box>
          ))}
      </Box>
    </Dialog>
  );
};

export default BarcodeModal;
